import clsx from 'clsx'
import { differenceInMilliseconds, isBefore, isWithinInterval } from 'date-fns'
import { useTranslation } from 'react-i18next'

import CheckCircle from '@/assets/icons/check-circle.svg?react'
import Clock from '@/assets/icons/clock.svg?react'
import ErrorCircle from '@/assets/icons/error-circle.svg?react'
import { useNow } from '@/hooks/useNow'
import i18n from '@/i18n'
import {
  getTimeDifference,
  type TimeDifference
} from '@/utils/get-time-difference'

import styles from './Counter.module.scss'

type CounterProps = { start: Date; end: Date; cancelled?: boolean }

const Counter = (props: CounterProps) => {
  const { t } = useTranslation('lessonDetails')
  const now = useNow()

  if (props.cancelled)
    return (
      <LabelAndValue
        variant="cancelled"
        label={t('text.lesson-status')}
        value={t('text.cancelled')}
        icon={<ErrorCircle className={styles.icon} />}
      />
    )
  if (isBefore(props.end, now))
    return (
      <LabelAndValue
        variant="finished"
        label={t('text.lesson-status')}
        value={t('text.finished')}
        icon={<CheckCircle className={styles.icon} />}
      />
    )
  if (isWithinInterval(now, { start: props.start, end: props.end }))
    return (
      <div>
        <LabelAndValue
          variant="current"
          label={t('text.lesson-time-left')}
          value={getTimeText(getTimeDifference(now, props.end))}
          icon={<Clock className={styles.icon} />}
        />
        <ProgressBar
          percentage={
            (differenceInMilliseconds(now, props.start) /
              differenceInMilliseconds(props.end, props.start)) *
            100
          }
        />
      </div>
    )
  return (
    <LabelAndValue
      variant="future"
      label={t('text.lesson-starts-in')}
      value={getTimeText(getTimeDifference(now, props.start))}
    />
  )
}

type LabelAndValueProps = {
  label: string
  value: React.ReactNode
  icon?: React.ReactNode
  variant: 'future' | 'current' | 'finished' | 'cancelled'
}

const LabelAndValue = (props: LabelAndValueProps) => (
  <div className={clsx(styles.container, styles[props.variant])}>
    <div className={styles.label}>{props.label}</div>
    <div className={styles.valueWrapper}>
      <div className={styles.value}>{props.value}</div>
      {props.icon}
    </div>
  </div>
)

const getTimeText = (timeDifference: TimeDifference) => {
  if ('days' in timeDifference)
    return i18n.t('text.x-days', {
      count: timeDifference.days,
      ns: 'lessonDetails'
    })
  if ('hours' in timeDifference && 'minutes' in timeDifference)
    return i18n.t('text.x-hours-x-minutes', {
      HOURS: timeDifference.hours,
      MINUTES: timeDifference.minutes,
      ns: 'lessonDetails'
    })
  if ('hours' in timeDifference)
    return i18n.t('text.x-hours', {
      HOURS: timeDifference.hours,
      ns: 'lessonDetails'
    })
  return i18n.t('text.x-minutes', {
    MINUTES: timeDifference.minutes,
    ns: 'lessonDetails'
  })
}

type ProgressBarProps = { percentage: number }
const ProgressBar = (props: ProgressBarProps) => (
  <div className={styles.progressBarWrapper}>
    <div
      className={styles.progressBarIndicator}
      style={{ width: `${props.percentage}%` }}
    />
  </div>
)

export default Counter
